import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import io from '../io';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import AssetHierarchyItem from './AssetHierarchyItem';

export const assetHierarchiesStore = getListBrowserStore({
  withQueryFilters: false,
  filterDefinitions: [
    {
      name: 'hierarchyProvider',
      caption: 'AssetHierarchy.List.Filter.HierarchyProvider',
      type: 'select',
      underlyingType: 'text'
    },
    {
      name: 'assetType',
      caption: 'AssetHierarchy.List.Filter.AssetType',
      type: 'select',
      underlyingType: 'number'
    }
  ],
  orderByFields: [
    {
      caption: 'AssetHierarchy.List.Filter.HierarchyProvider',
      value: 'hierarchyProvider'
    }
  ],
  pager: {
    orderBy: 'hierarchyProvider',
    direction: 'asc'
  }
});

const AssetHierarchyBrowser = ({ title, hierarchyActions, events }) => {
  const { t } = useTranslation();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: io.queries.ASSET_HIERARCHY_LIST_QUERY,
        name: 'assetHierarchies'
      },
      collectionsOptions: [
        {
          queryOptions: { query: io.queries.ASSET_HIERARCHY_SOURCES_QUERY },
          name: 'assetHierarchySources',
          identifier: 'hierarchyProvider',
          collectionType: CollectionType.Filter,
          valueKey: 'name',
          labelKey: 'name'
        },
        {
          queryOptions: { query: io.queries.ASSET_TYPES_QUERY },
          name: 'assetHierarchyTypes',
          identifier: 'assetType',
          collectionType: CollectionType.Filter,
          valueKey: 'id',
          labelKey: 'name'
        }
      ]
    }),
    []
  );

  const itemComponent = useCallback(
    ({ data }) => <AssetHierarchyItem data={data} hierarchyActions={hierarchyActions} events={events} />,
    [events, hierarchyActions]
  );

  const columns = useMemo(
    () => [
      { name: 'assetType', caption: 'AssetHierarchy.List.Row.AssetType' },
      { name: 'name', caption: 'AssetHierarchy.List.Row.Name' },
      { name: 'hierarchyProvider', caption: 'AssetHierarchy.List.Row.HierarchyProvider' },
      { name: 'actions', caption: 'Actions', width: '100px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon} title={title ?? t('AssetHierarchy.List.Title')}>
      <TableListBrowser name='asset-hierarchy-browser' store={assetHierarchiesStore} ItemComponent={itemComponent} columns={columns} />
      <ExportModalComponent store={assetHierarchiesStore} options={fetchingOptions} />
      <ApolloFetchingComponent store={assetHierarchiesStore} options={fetchingOptions} />
    </Card>
  );
};

AssetHierarchyBrowser.propTypes = {
  title: PropTypes.node,
  hierarchyActions: PropTypes.array.isRequired,
  events: PropTypes.object.isRequired
};

export default AssetHierarchyBrowser;
